import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Mail, Loader2, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const { forgotPassword } = useAuth();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await forgotPassword(email);
            setSent(true);
            toast.success('Se l\'email esiste, riceverai un link per il reset');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Errore nell\'invio');
        } finally {
            setLoading(false);
        }
    };

    if (sent) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F2F5F2] p-4">
                <Card className="w-full max-w-md border border-[#CBD5E1] shadow-none">
                    <CardHeader className="space-y-1 text-center">
                        <CheckCircle className="h-12 w-12 text-[#34C759] mx-auto mb-4" />
                        <CardTitle className="text-2xl text-[#0F172A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                            Email inviata!
                        </CardTitle>
                        <CardDescription className="text-[#64748B]">
                            Se l'indirizzo email è associato a un account, riceverai un link per reimpostare la password.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <Link to="/login">
                            <Button variant="outline" className="w-full border-[#CBD5E1]">
                                <ArrowLeft className="h-4 w-4 mr-2" />
                                Torna al login
                            </Button>
                        </Link>
                    </CardContent>
                </Card>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-[#F2F5F2] p-4">
            <Card className="w-full max-w-md border border-[#CBD5E1] shadow-none">
                <CardHeader className="space-y-1">
                    <div className="flex items-center gap-2 mb-2">
                        <Mail className="h-8 w-8 text-[#1B4E8E]" />
                    </div>
                    <CardTitle className="text-2xl text-[#0F172A]" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                        Recupera password
                    </CardTitle>
                    <CardDescription className="text-[#64748B]">
                        Inserisci l'email associata al tuo account per ricevere un link di reset.
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email" className="text-[#0F172A]">Email</Label>
                            <Input
                                id="email"
                                data-testid="forgot-password-email-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="tua@email.com"
                                required
                                className="border-[#CBD5E1] focus:ring-[#1B4E8E]"
                            />
                        </div>
                        <Button 
                            type="submit" 
                            data-testid="forgot-password-submit-btn"
                            className="w-full bg-[#1B4E8E] hover:bg-[#143A6B] text-white"
                            disabled={loading}
                        >
                            {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                            Invia link di reset
                        </Button>
                    </form>
                    <div className="mt-4 text-center">
                        <Link to="/login" className="text-sm text-[#1B4E8E] hover:underline flex items-center justify-center gap-1">
                            <ArrowLeft className="h-4 w-4" />
                            Torna al login
                        </Link>
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
